import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  buildTestHistory,
  fetchCardData,
  fetchCaseExecutionHistory,
  fetchCaseExecutions,
  fetchEnvironmentExecutionReport,
  fetchEnvironmentReport,
  fetchExecutionHistory,
  fetchExecutionSummary,
  fetchPlanExecutions,
  fetchSuiteExecutions,
  fetchSuiteReport,
  summarizeHistory,
} from './reportData';

export const REPORT_TYPES = ['summary', 'detailed', 'failures'];

const DEFAULTS = {
  type: 'summary',
  project: '',
  range: 'last30',
  from: '',
  to: '',

  // Summary — suite health + recent executions
  s_page: 1,
  s_size: 10,
  s_q: '',
  s_sort: '',
  h_page: 1,
  h_size: 10,

  // Detailed — active tab + one list state per tab
  view: 'cases',
  c_page: 1,
  c_size: 20,
  c_q: '',
  c_status: 'all',
  c_sort: '',
  r_page: 1,
  r_size: 10,
  r_q: '',
  r_type: 'all',
  r_sort: '',
  p_page: 1,
  p_size: 10,
  p_q: '',
  p_sort: '',
  e_sort: '',

  // Failures
  f_page: 1,
  f_size: 20,
  f_q: '',
  f_sort: '',
};

const PAGE_KEYS = Object.keys(DEFAULTS).filter((k) => k.endsWith('_page'));
const GLOBAL_KEYS = ['project', 'range', 'from', 'to'];

/** Changing any of these list inputs sends that list back to page 1. */
const PAGE_RESET = {
  s_q: 's_page',
  s_size: 's_page',
  h_size: 'h_page',
  c_q: 'c_page',
  c_status: 'c_page',
  c_size: 'c_page',
  r_q: 'r_page',
  r_type: 'r_page',
  r_size: 'r_page',
  p_q: 'p_page',
  p_size: 'p_page',
  f_q: 'f_page',
  f_size: 'f_page',
};

export const FILTER_LABELS = {
  s_q: 'Suite search',
  c_q: 'Test case search',
  c_status: 'Status',
  r_q: 'Suite run search',
  r_type: 'Executed via',
  p_q: 'Plan search',
  f_q: 'Failure search',
};

export const FILTERS_BY_TYPE = {
  summary: ['s_q'],
  detailed: ['c_q', 'c_status', 'r_q', 'r_type', 'p_q'],
  failures: ['f_q'],
};

export function useReportParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const out = {};
    Object.entries(DEFAULTS).forEach(([key, def]) => {
      const raw = searchParams.get(key);
      if (raw == null || raw === '') {
        out[key] = def;
      } else if (typeof def === 'number') {
        const n = Number(raw);
        out[key] = Number.isFinite(n) && n > 0 ? n : def;
      } else {
        out[key] = raw;
      }
    });
    if (!REPORT_TYPES.includes(out.type)) out.type = DEFAULTS.type;
    return out;
  }, [searchParams]);

  const update = useCallback(
    (patch, { push = false } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const apply = { ...patch };

          if (Object.keys(patch).some((k) => GLOBAL_KEYS.includes(k))) {
            PAGE_KEYS.forEach((k) => {
              if (!(k in patch)) apply[k] = 1;
            });
          }
          Object.keys(patch).forEach((k) => {
            const pageKey = PAGE_RESET[k];
            if (pageKey && !(pageKey in patch)) apply[pageKey] = 1;
          });

          Object.entries(apply).forEach(([key, value]) => {
            const def = DEFAULTS[key];
            if (value == null || value === '' || String(value) === String(def)) next.delete(key);
            else next.set(key, String(value));
          });
          return next;
        },
        { replace: !push }
      );
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(
    (type) => {
      const patch = { range: DEFAULTS.range, from: '', to: '' };
      (FILTERS_BY_TYPE[type] || []).forEach((k) => {
        patch[k] = DEFAULTS[k];
      });
      update(patch);
    },
    [update]
  );

  return { params, update, clearFilters, defaults: DEFAULTS };
}

export const REPORTS_QUERY_ROOT = 'reports';

const STALE_MS = 60 * 1000;

const keyOf = (ctx, name, extra = {}) => [
  REPORTS_QUERY_ROOT,
  name,
  ctx.projectId,
  ctx.payload?.date_range,
  ctx.payload?.start_date,
  ctx.payload?.end_date,
  extra,
];

const useReportQuery = (ctx, name, fn, extra, { paged = false, select } = {}) =>
  useQuery({
    queryKey: keyOf(ctx, name, extra),
    queryFn: () => fn(ctx.payload, extra),
    enabled: Boolean(ctx.enabled),
    staleTime: STALE_MS,
    placeholderData: paged ? keepPreviousData : undefined,
    retry: 1,
    refetchOnWindowFocus: false,
    select,
  });

export const useCardData = (ctx) => useReportQuery(ctx, 'card', fetchCardData, {});

export const useEnvironmentReport = (ctx) =>
  useReportQuery(ctx, 'environments', fetchEnvironmentReport, {});

export const useExecutionSummary = (ctx) =>
  useReportQuery(ctx, 'execSummary', fetchExecutionSummary, {});

export const useSuiteReport = (ctx, { page, pageSize, search }) =>
  useReportQuery(ctx, 'suites', fetchSuiteReport, { page, pageSize, search }, { paged: true });

export const useExecutionHistory = (ctx, { page, pageSize }) =>
  useReportQuery(ctx, 'history', fetchExecutionHistory, { page, pageSize }, { paged: true });

/** Up to 100 day-level rows — enough for any preset and most custom ranges. */
export const useDailyExecutionTrend = (ctx) =>
  useReportQuery(ctx, 'history', fetchExecutionHistory, { page: 1, pageSize: 100 });

/** Case report with no filters: carries the backend's per-suite status chart and totals. */
export const useCaseOverview = (ctx) =>
  useReportQuery(ctx, 'caseOverview', fetchCaseExecutions, { page: 1, pageSize: 1 });

export const useCaseExecutions = (ctx, { page, pageSize, search, status }) =>
  useReportQuery(
    ctx,
    'cases',
    fetchCaseExecutions,
    { page, pageSize, search, status },
    { paged: true }
  );

export const useSuiteExecutions = (ctx, { page, pageSize, search, executionType }) =>
  useReportQuery(
    ctx,
    'suiteRuns',
    fetchSuiteExecutions,
    { page, pageSize, search, executionType },
    { paged: true }
  );

export const usePlanExecutions = (ctx, { page, pageSize, search }) =>
  useReportQuery(ctx, 'planRuns', fetchPlanExecutions, { page, pageSize, search }, { paged: true });

export const useEnvironmentExecutionReport = (ctx) =>
  useReportQuery(ctx, 'envExecutions', fetchEnvironmentExecutionReport, {});

const selectHistory = (data) => {
  const map = buildTestHistory(data.rows);
  return { ...data, map, ...summarizeHistory(map) };
};

export const useTestHistory = (ctx) =>
  useReportQuery(ctx, 'testHistory', fetchCaseExecutionHistory, {}, { select: selectHistory });

/**
 * Local input state for a URL-backed search param: typing stays instant while
 * the URL (and therefore the API request) updates after a pause.
 */
export function useDebouncedUrlSearch(urlValue, commit, delay = 400) {
  const [value, setValue] = useState(urlValue || '');
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const lastCommitted = useRef(urlValue || '');

  useEffect(() => {
    if ((urlValue || '') !== lastCommitted.current) {
      lastCommitted.current = urlValue || '';
      setValue(urlValue || '');
    }
  }, [urlValue]);

  useEffect(() => {
    const next = value.trim();
    if (next === lastCommitted.current) return undefined;
    const t = setTimeout(() => {
      lastCommitted.current = next;
      commitRef.current(next);
    }, delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return [value, setValue];
}


